import { appConfig } from '../../config';
import { ApiResponse } from '../../types';

// Event emitter for authentication errors
export const authErrorEvent = new EventTarget();

class ApiClient {
  private baseUrl: string;
  private defaultHeaders: Record<string, string>;
  private enableLogging: boolean;

  constructor() {
    // In development, use proxy configured in package.json
    // In production, use full API base URL
    this.baseUrl = appConfig.env === 'development' ? '' : appConfig.apiBaseUrl;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
    // Enable logging in development
    this.enableLogging = appConfig.env === 'development';
  }

  /**
   * Handle authentication errors (401/403)
   * Clears tokens and dispatches auth error event
   */
  private handleAuthError(statusCode: number): void {
    if (statusCode === 401) {
      // 401 = token expired / invalid → clear session and redirect to login
      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
      }

      const event = new CustomEvent('authError', {
        detail: {
          statusCode,
          message: 'Token expired or invalid',
          timestamp: new Date().toISOString(),
        },
      });
      authErrorEvent.dispatchEvent(event);

      console.warn(
        `%c🔐 Authentication Error [${statusCode}]`,
        'color: #ff6600; font-weight: bold;',
        'Redirecting to login...'
      );
    } else if (statusCode === 403) {
      // 403 = authenticated but forbidden → do NOT clear tokens, just log
      console.warn(
        `%c🚫 Forbidden [403]`,
        'color: #cc0000; font-weight: bold;',
        'User does not have permission for this resource.'
      );
    }
  }

  /**
   * Log request details
   */
  private logRequest(method: string, url: string, headers: Record<string, string>, body?: any): void {
    if (!this.enableLogging) return;

    const fullUrl = `${this.baseUrl}${url}`;
    const timestamp = new Date().toISOString();

    console.group(`%c📤 ${method} ${url}`, 'color: #0066cc; font-weight: bold; font-size: 12px;');
    console.log(`%cTimestamp: ${timestamp}`, 'color: #666; font-size: 11px;');
    console.log(`%cFull URL: ${fullUrl}`, 'color: #666; font-size: 11px;');
    console.log('%cHeaders:', 'color: #008000; font-weight: bold;');
    console.table(headers);
    
    if (body) {
      console.log('%cBody:', 'color: #008000; font-weight: bold;');
      console.log(body);
    }
    console.groupEnd();
  }

  /**
   * Log response details
   */
  private async logResponse(
    method: string,
    url: string,
    response: Response,
    data: any,
    duration: number
  ): Promise<void> {
    if (!this.enableLogging) return;

    const statusColor = response.ok ? '#28a745' : '#dc3545';
    const timestamp = new Date().toISOString();

    console.group(
      `%c📥 ${method} ${url} [${response.status}]`,
      `color: ${statusColor}; font-weight: bold; font-size: 12px;`
    );
    console.log(`%cTimestamp: ${timestamp}`, 'color: #666; font-size: 11px;');
    console.log(`%cStatus: ${response.status} ${response.statusText}`, `color: ${statusColor}; font-weight: bold;`);
    console.log(`%cDuration: ${duration}ms`, 'color: #ff6600; font-size: 11px;');
    console.log('%cHeaders:', 'color: #008000; font-weight: bold;');
    
    const responseHeaders: Record<string, string> = {};
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value;
    });
    console.table(responseHeaders);

    console.log('%cResponse Data:', 'color: #008000; font-weight: bold;');
    console.log(data);
    console.groupEnd();
  }

  /**
   * Get authorization token from localStorage
   */
  private getAuthToken(): string | null {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('accessToken');
    }
    return null;
  }

  /**
   * Build headers with auth token if available
   */
  private getHeaders(): Record<string, string> {
    const headers = { ...this.defaultHeaders };
    const token = this.getAuthToken();

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string, useAuth: boolean = false): Promise<ApiResponse<T>> {
    try {
      const headers = useAuth ? this.getHeaders() : this.defaultHeaders;
      const startTime = performance.now();
      
      this.logRequest('GET', endpoint, headers);

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'GET',
        headers,
      });

      const data = await response.json();
      const duration = Math.round(performance.now() - startTime);

      await this.logResponse('GET', endpoint, response, data, duration);

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        this.handleAuthError(response.status);
        return {
          statusCode: response.status,
          data: null,
          error: response.status === 401 ? 'Token expired or invalid' : 'Access forbidden',
        };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`%c❌ GET ${endpoint} failed`, 'color: #dc3545; font-weight: bold;', error);
      return {
        statusCode: 500,
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * POST request
   */
  async post<T>(
    endpoint: string,
    body: Record<string, any>,
    useAuth: boolean = false
  ): Promise<ApiResponse<T>> {
    try {
      const headers = useAuth ? this.getHeaders() : this.defaultHeaders;
      const startTime = performance.now();

      this.logRequest('POST', endpoint, headers, body);

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const data = await response.json();
      const duration = Math.round(performance.now() - startTime);

      await this.logResponse('POST', endpoint, response, data, duration);

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        this.handleAuthError(response.status);
        return {
          statusCode: response.status,
          data: null,
          error: response.status === 401 ? 'Token expired or invalid' : 'Access forbidden',
        };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`%c❌ POST ${endpoint} failed`, 'color: #dc3545; font-weight: bold;', error);
      return {
        statusCode: 500,
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * PUT request
   */
  async put<T>(
    endpoint: string,
    body: Record<string, any>,
    useAuth: boolean = true
  ): Promise<ApiResponse<T>> {
    try {
      const headers = useAuth ? this.getHeaders() : this.defaultHeaders;
      const startTime = performance.now();

      this.logRequest('PUT', endpoint, headers, body);

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(body),
      });

      const data = await response.json();
      const duration = Math.round(performance.now() - startTime);

      await this.logResponse('PUT', endpoint, response, data, duration);

      if (response.status === 401 || response.status === 403) {
        this.handleAuthError(response.status);
        return {
          statusCode: response.status,
          data: null,
          error: response.status === 401 ? 'Token expired or invalid' : 'Access forbidden',
        };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`%c❌ PUT ${endpoint} failed`, 'color: #dc3545; font-weight: bold;', error);
      return {
        statusCode: 500,
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * PATCH request
   */
  async patch<T>(
    endpoint: string,
    body: Record<string, any>,
    useAuth: boolean = true
  ): Promise<ApiResponse<T>> {
    try {
      const headers = useAuth ? this.getHeaders() : this.defaultHeaders;
      const startTime = performance.now();

      this.logRequest('PATCH', endpoint, headers, body);

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });

      const data = await response.json();
      const duration = Math.round(performance.now() - startTime);

      await this.logResponse('PATCH', endpoint, response, data, duration);

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        this.handleAuthError(response.status);
        return {
          statusCode: response.status,
          data: null,
          error: response.status === 401 ? 'Token expired or invalid' : 'Access forbidden',
        };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`%c❌ PATCH ${endpoint} failed`, 'color: #dc3545; font-weight: bold;', error);
      return {
        statusCode: 500,
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string, useAuth: boolean = true): Promise<ApiResponse<T>> {
    try {
      const headers = useAuth ? this.getHeaders() : this.defaultHeaders;
      const startTime = performance.now();

      this.logRequest('DELETE', endpoint, headers);

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: 'DELETE',
        headers,
      });

      const data = await response.json();
      const duration = Math.round(performance.now() - startTime);

      await this.logResponse('DELETE', endpoint, response, data, duration);

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        this.handleAuthError(response.status);
        return {
          statusCode: response.status,
          data: null,
          error: response.status === 401 ? 'Token expired or invalid' : 'Access forbidden',
        };
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error(`%c❌ DELETE ${endpoint} failed`, 'color: #dc3545; font-weight: bold;', error);
      return {
        statusCode: 500,
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
}

export const apiClient = new ApiClient();
